import { getToolName, isToolUIPart, type UIMessage } from "ai";

export type ToolCallApprovalPayload = {
  runId: string;
  toolCallId: string;
  toolName: string;
  args: Record<string, unknown>;
};

function isPendingWriteToolPart(
  part: Extract<UIMessage["parts"][number], { toolCallId: string }>
): boolean {
  return (
    part.state === "input-available" ||
    part.state === "approval-requested" ||
    (part.state === "input-streaming" && Boolean(part.input))
  );
}

export function findActiveWriteApproval<TProposal extends Record<string, unknown>>({
  messages,
  approvalByToolCallId,
  isWriteToolName,
  normalizeProposalInput,
  resolvedToolCallIds,
}: {
  messages: UIMessage[];
  approvalByToolCallId: Record<string, ToolCallApprovalPayload>;
  isWriteToolName: (toolName: string) => boolean;
  normalizeProposalInput: (raw: unknown) => TProposal;
  resolvedToolCallIds?: ReadonlySet<string>;
}): {
  toolCallId: string;
  runId: string;
  toolName: string;
  mode: "create" | "update";
  proposal: TProposal;
  inputData: TProposal;
  approval: ToolCallApprovalPayload | null;
  approvalReady: boolean;
} | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message.role !== "assistant") continue;
    for (let j = message.parts.length - 1; j >= 0; j--) {
      const part = message.parts[j];
      if (!isToolUIPart(part)) continue;
      if (!isPendingWriteToolPart(part)) continue;
      const toolName = getToolName(part);
      if (!isWriteToolName(toolName)) continue;
      const toolCallId = part.toolCallId;
      if (resolvedToolCallIds?.has(toolCallId)) continue;
      const approval = approvalByToolCallId[toolCallId];
      const inputData = normalizeProposalInput(part.input);
      const approvalReady = Boolean(approval?.runId);
      if (!approvalReady && !part.input) continue;
      const proposal = (
        approvalReady && approval?.args && "id" in approval.args && approval.args.id
          ? approval.args
          : inputData
      ) as TProposal;
      const mode = toolName.toLowerCase().includes("update") ? "update" : "create";
      return {
        toolCallId,
        runId: approval?.runId ?? "",
        toolName,
        mode,
        proposal,
        inputData,
        approval: approvalReady ? approval! : null,
        approvalReady,
      };
    }
  }
  return null;
}
